/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { IsFocusVisibleClassName, classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useMountLogger } from 'src/core/hooks/useMountLogger';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';

export type BladeProps = {
    /**
     * The name of the blade for telemetry logging purposes.
     */
    activitySource: string;

    /**
     * The blade header content.
     */
    header: React.ReactNode;

    /**
     * The main blade content.
     */
    content: React.ReactNode;

    /**
     * Whether the blade in dark theme or not.
     */
    isDarkTheme?: boolean;

    /**
     * Whether to show the close button or not.
     */
    hasCloseButton?: boolean;

    /**
     * The blade width.
     */
    width?: number;

    /**
     * Determines whether the blade have the full width or not.
     */
    fullWidth?: boolean;

    /**
     * An optional class to apply to the blade.
     */
    className?: string;

    /**
     * A callback for when the blade
     * is dismissed.
     */
    onDismiss?: () => void;
};

const messages = defineMessages({
    closeButtonTitle: {
        id: 'core.blade.closeButtonTitle',
        defaultMessage: 'Close pane'
    }
});

const { styleDiv, styleComponent } = getStylist('Blade');
const bladeTheme = getScopedTheme('Blade');

const BladeContainer = styleComponent<IStackProps>(Stack)<{ width: number; fullWidth: boolean }>(
    'BladeContainer',
    theme => ({ overflowY: 'hidden', padding: theme.contentPadding }),
    ({ customProps }) => ({
        width: customProps.fullWidth ? '100%' : customProps.width
    })
);
const BladeHeader = styleComponent<IStackProps>(Stack)('BladeHeader', theme => ({ height: theme.headerHeight }));
const BladeTitle = styleComponent<LuisTextProps>(LuisText)('BladeTitle', {
    flex: 1,
    height: '100%',
    paddingTop: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
});
const BladeContent = styleDiv('BladeContent', { display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto', overflowX: 'auto' });
const closeButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ icon: { fontSize: bladeTheme.closeIconSize } });
const closeButtonDarkStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    rootHovered: { backgroundColor: bladeTheme.hoveredCloseButtonBackground },
    icon: { fontSize: bladeTheme.closeIconSize, color: bladeTheme.closeIconDarkThemeColor }
});

/**
 * Represents a container that blade host renders each blade within
 *
 * @param props Properties that include blade content.
 */
export const Blade = (props: BladeProps) => {
    const { activitySource, fullWidth, header, content, width, className, onDismiss, hasCloseButton = true, isDarkTheme = false } = props;
    const { localize } = useLocalization();
    useMountLogger({ activitySource });

    // Gets random ids for aria attributes.
    const closeButtonId = React.useRef(getId('iconButton'));

    return (
        <BladeContainer verticalFill customProps={{ width, fullWidth }} className={className}>
            {header && (
                <BladeHeader horizontal verticalAlign="center">
                    <BladeTitle as="h1">{header}</BladeTitle>

                    {hasCloseButton ? (
                        <LuisTooltip
                            hidden={document.body.className.indexOf(IsFocusVisibleClassName) === -1}
                            content={localize(messages.closeButtonTitle)}>
                            <IconButton
                                id={closeButtonId.current}
                                styles={isDarkTheme ? closeButtonDarkStyles : closeButtonStyles}
                                iconProps={{ iconName: 'ChromeClose' }}
                                title={localize(messages.closeButtonTitle)}
                                ariaLabel={localize(messages.closeButtonTitle)}
                                onClick={onDismiss}
                                data-automation-id="CloseBladeButton"
                                autoFocus
                            />
                        </LuisTooltip>
                    ) : null}
                </BladeHeader>
            )}

            <BladeContent>{content}</BladeContent>
        </BladeContainer>
    );
};
